/**
 * TBRPreview — a compact virtual-shelf preview, not the full Bookshelf page.
 * Covers rest on a subtle shelf line; a single link hands off to the real
 * TBR shelf for anything beyond a quick glance.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { SectionHeader } from '@/components/site/primitives';
import { fadeUp, stagger, viewport } from '@/lib/motion';
import { bookHref } from '@/lib/bookModel';
import BookCover from '@/components/books/BookCoverArt';
import EmptyState from './EmptyState';
import { PROFILE } from '@/constants/testIds';

export default function TBRPreview({ books, total }) {
  const preview = books.slice(0, 8);

  return (
    <section className="u-container mt-16 md:mt-24" data-testid={PROFILE.tbrPreview}>
      <div className="flex items-end justify-between gap-4">
        <SectionHeader eyebrow="Up next" title="My TBR" />
        <Link to="/app/bookshelf?shelf=shelf_tbr" className="group mb-1 hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-coral sm:flex">
          View Bookshelf <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-soft group-hover:translate-x-1" />
        </Link>
      </div>

      {preview.length === 0 ? (
        <EmptyState type="tbr" to="/app/explore" compact className="mt-2" />
      ) : (
        <div className="mt-8">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="flex gap-3.5 overflow-x-auto pb-2 scrollbar-none"
          >
            {preview.map((book) => (
              <motion.div key={book.id} variants={fadeUp} className="w-20 shrink-0 sm:w-24">
                <Link to={bookHref(book.id)}>
                  <BookCover book={book} className="w-full" />
                </Link>
              </motion.div>
            ))}
            {total > preview.length && (
              <motion.div variants={fadeUp} className="flex w-20 shrink-0 items-end justify-center pb-1 text-xs font-semibold text-subtle sm:w-24">
                +{total - preview.length} more
              </motion.div>
            )}
          </motion.div>
          <div className="mt-1 h-2 rounded-full bg-line/60" aria-hidden="true" />
        </div>
      )}

      <Link to="/app/bookshelf?shelf=shelf_tbr" className="group mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-coral sm:hidden">
        View Bookshelf <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-soft group-hover:translate-x-1" />
      </Link>
    </section>
  );
}
